import React from "react";
import PropTypes from "prop-types";
import noop from "lodash/noop";
import {
  Button,
  Form,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  FormGroup,
  Row,
  Col,
  Label,
  UncontrolledTooltip,
} from "reactstrap";

import TermsAndConditionsInput from "../Inputs/TermsAndConditions";
import { formatAddress, titleCase } from "../../utils";
import { SUPPORT_EMAIL, MAP_BBOX_OFFSET } from "../../constants";
import { yearBuiltMaxPlausible } from "../../lib/dateUtils";
import {
  CertifyBlock,
  FieldError,
  FieldShell,
  FormHeading,
  FormPanel,
  MapColumn,
  MapContainer,
  MapIframe,
  MapNote,
  PanelBody,
  SubmitWrapper,
} from "./styled";

const VerifyAddressForm = ({
  values,
  selectedAddress,
  errors,
  handleChange,
  handleCertifyInformationChange,
  isInformationCertified,
  handlePrimaryResidenceChange,
  isPrimaryResidence,
  onChangeCity,
  handleSubmit,
  disableConfirmation,
  disableAll,
  submitButtonText,
  termsAndConditions,
  handleChangeTermsAndConditions,
  submitButtonDisabled,
  handleToggleTermsAndConditionsModal,
  AdditionalFields,
  // Slot for content that should sit INSIDE the left column of the
  // panel body, immediately under the year-built / city row.  Used
  // by /verify to render the "this is my primary residence" +
  // "have a discount code?" inline footnote, the email field, and
  // the T&Cs check tucked against the property facts (rather than
  // floating in their own sections below the whole panel body).
  belowFieldsAddon,
  // Slot for content that should sit INSIDE the right column,
  // beneath the map.  Lets /verify put a quiet reassurance note
  // there so the right rail balances the left column's height.
  belowMapAddon,
  // Replaces the default FormHeading.  When provided, the caller
  // owns the heading/eyebrow/intro copy.  /verify uses this to
  // collapse its (formerly separate) FormStep eyebrow + heading +
  // back-link into the panel chrome — one coral hairline, one
  // panel, one heading instead of nested cards.
  headingSlot,
}) => {
  const { bedCount, bathCount, buildingSqFt, lotSqFt, yearBuilt, city } =
    values;

  const lotSizeReadonly = lotSqFt === 0;

  const renderPropertyMap = () => {
    if (
      !selectedAddress ||
      !selectedAddress.latitude ||
      !selectedAddress.longitude
    )
      return null;

    const { latitude, longitude } = selectedAddress;
    const bbox = [
      longitude - MAP_BBOX_OFFSET,
      latitude - MAP_BBOX_OFFSET,
      longitude + MAP_BBOX_OFFSET,
      latitude + MAP_BBOX_OFFSET,
    ];
    const iframeSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox[0]}%2C${bbox[1]}%2C${bbox[2]}%2C${bbox[3]}&layer=mapnik&marker=${latitude}%2C${longitude}`;

    return (
      <MapContainer>
        <MapIframe
          frameBorder="0"
          title="osm-address"
          marginHeight="0"
          marginWidth="0"
          src={iframeSrc}
        />
        <MapNote>
          Wrong location or ZIP? Email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll fix it.
        </MapNote>
      </MapContainer>
    );
  };

  return (
    <Form className="register-form">
      <FormPanel>
        {headingSlot ? (
          headingSlot
        ) : (
          <FormHeading>
            <h3>Confirm your property details</h3>
            <small>Edit anything that doesn't match.</small>
          </FormHeading>
        )}

        {/* Address row — full width across the panel above the
            two-column body so the user always sees the parsed
            address as the canonical anchor. */}
        <FormGroup>
          <FieldShell $disabled $stacked>
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>Address</InputGroupText>
              </InputGroupAddon>
              <Input
                placeholder="...."
                type="address"
                value={formatAddress(selectedAddress)}
                disabled
                aria-label="Property address"
              />
            </InputGroup>
          </FieldShell>
        </FormGroup>

        <PanelBody>
          {/* Left rail — the editable property facts. Three rows
              of paired inputs sized down evenly so the eye reads
              this as a tidy table rather than a stack of cards. */}
          <div>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <FieldShell
                    $stacked
                    $disabled={disableAll}
                    $hasError={!!errors.bedCount}
                  >
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>Bedrooms</InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder="#"
                        value={bedCount}
                        name="bedCount"
                        onChange={handleChange}
                        type="number"
                        required
                        min="0"
                        max="15"
                        step="1"
                        disabled={disableAll}
                        aria-label="Number of bedrooms"
                        aria-invalid={!!errors.bedCount}
                      />
                    </InputGroup>
                  </FieldShell>
                  {errors.bedCount && (
                    <FieldError>{errors.bedCount}</FieldError>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <FieldShell
                    $stacked
                    $disabled={disableAll}
                    $hasError={!!errors.bathCount}
                  >
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>Bathrooms</InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder="#"
                        value={bathCount}
                        name="bathCount"
                        onChange={handleChange}
                        type="number"
                        required
                        min="0"
                        max="15"
                        step="0.5"
                        disabled={disableAll}
                        aria-label="Number of bathrooms"
                        aria-invalid={!!errors.bathCount}
                      />
                    </InputGroup>
                  </FieldShell>
                  {errors.bathCount && (
                    <FieldError>{errors.bathCount}</FieldError>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <FieldShell
                    $stacked
                    $disabled={disableAll}
                    $hasError={!!errors.buildingSqFt}
                  >
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>Sq. Ft</InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder="#"
                        value={buildingSqFt}
                        name="buildingSqFt"
                        onChange={handleChange}
                        type="number"
                        required
                        min="100"
                        max="50000"
                        disabled={disableAll}
                        aria-label="Building square footage"
                        aria-invalid={!!errors.buildingSqFt}
                      />
                    </InputGroup>
                  </FieldShell>
                  {errors.buildingSqFt && (
                    <FieldError>{errors.buildingSqFt}</FieldError>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <FieldShell
                    $stacked
                    $disabled={disableAll && !lotSizeReadonly}
                    $readonlyHelp={lotSizeReadonly}
                    $hasError={!!errors.lotSqFt}
                    id="LotSizeTooltip"
                  >
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>Lot (Sq. Ft.)</InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder="#"
                        value={lotSqFt}
                        name="lotSqFt"
                        onChange={handleChange}
                        type="number"
                        required
                        min="0"
                        max="10000000"
                        disabled={disableAll || lotSizeReadonly}
                        aria-label="Lot size in square feet"
                        aria-invalid={!!errors.lotSqFt}
                      />
                    </InputGroup>
                  </FieldShell>
                  {errors.lotSqFt && (
                    <FieldError>{errors.lotSqFt}</FieldError>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md={6}>
                <FormGroup>
                  <FieldShell
                    $stacked
                    $disabled={disableAll}
                    $hasError={!!errors.yearBuilt}
                  >
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>Year Built</InputGroupText>
                      </InputGroupAddon>
                      <Input
                        min={1700}
                        max={yearBuiltMaxPlausible()}
                        placeholder="2018"
                        value={yearBuilt}
                        name="yearBuilt"
                        onChange={handleChange}
                        type="number"
                        required
                        disabled={disableAll}
                        aria-label="Year built"
                        aria-invalid={!!errors.yearBuilt}
                      />
                    </InputGroup>
                  </FieldShell>
                  {errors.yearBuilt && (
                    <FieldError>{errors.yearBuilt}</FieldError>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <FieldShell
                    $stacked
                    $disabled={disableAll}
                    $hasError={!!errors.city}
                  >
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>City</InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder="Austin"
                        value={titleCase(city)}
                        name="city"
                        onChange={onChangeCity}
                        type="text"
                        required
                        disabled={disableAll}
                        aria-label="City"
                        aria-invalid={!!errors.city}
                      />
                    </InputGroup>
                  </FieldShell>
                  {errors.city && <FieldError>{errors.city}</FieldError>}
                </FormGroup>
              </Col>
            </Row>
            {/* Tucked-in addon row right under year-built / city —
                used by /verify to keep the "primary residence"
                checkbox + "have a discount code?" link visually
                attached to the property facts. */}
            {belowFieldsAddon}
          </div>

          {/* Right rail — the property map. On mobile this column
              actually renders ABOVE the fields (CSS grid order
              swap on small screens) so the user gets a visual
              anchor before the data-entry ask.  An optional
              ``belowMapAddon`` lets the caller stack content
              (e.g. a reassurance note) under the map so the
              right column reads as a complete unit. */}
          <MapColumn>
            {renderPropertyMap()}
            {belowMapAddon}
          </MapColumn>
        </PanelBody>

        {!disableConfirmation && (
          <CertifyBlock>
            <FormGroup>
              <Label className="certify-information-label" check>
                <Input
                  type="checkbox"
                  onChange={handlePrimaryResidenceChange}
                  checked={!!isPrimaryResidence}
                />{" "}
                This is my primary home (place where I live).
              </Label>
            </FormGroup>
            <FormGroup>
              <Label className="certify-information-label" check>
                <Input
                  type="checkbox"
                  onChange={handleCertifyInformationChange}
                  checked={isInformationCertified}
                />{" "}
                The information above is accurate.
              </Label>
            </FormGroup>
          </CertifyBlock>
        )}

        {AdditionalFields}
        {termsAndConditions && (
          <TermsAndConditionsInput
            onTermsConditionsClick={handleToggleTermsAndConditionsModal}
            onChange={handleChangeTermsAndConditions}
            error={errors.agreeTermsAndConditions}
          />
        )}

        <SubmitWrapper disabled={!isInformationCertified}>
          <Button
            className={`btn-round ${submitButtonDisabled ? "not-allowed" : ""}`}
            color="neutral"
            onClick={handleSubmit}
            type="submit"
            disabled={submitButtonDisabled}
          >
            {submitButtonText} <i className="fa fa-chevron-right" />
          </Button>
        </SubmitWrapper>

        <UncontrolledTooltip placement="top" target="LotSizeTooltip">
          When county data shows this value is zero, it generally indicates
          that your home's appraised value does not take lot size into
          consideration.
        </UncontrolledTooltip>
      </FormPanel>
    </Form>
  );
};

VerifyAddressForm.propTypes = {
  values: PropTypes.object,
  selectedAddress: PropTypes.object,
  errors: PropTypes.object,
  handleChange: PropTypes.func,
  handleCertifyInformationChange: PropTypes.func,
  isInformationCertified: PropTypes.bool,
  handlePrimaryResidenceChange: PropTypes.func,
  isPrimaryResidence: PropTypes.bool,
  onChangeCity: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  disableConfirmation: PropTypes.bool,
  disableAll: PropTypes.bool,
  submitButtonText: PropTypes.string,
  termsAndConditions: PropTypes.bool,
  handleChangeTermsAndConditions: PropTypes.func,
  agreeTermsAndConditions: PropTypes.bool,
  handleToggleTermsAndConditionsModal: PropTypes.func,
  AdditionalFields: PropTypes.node,
  belowFieldsAddon: PropTypes.node,
  belowMapAddon: PropTypes.node,
  headingSlot: PropTypes.node,
};

VerifyAddressForm.defaultProps = {
  values: {},
  errors: {},
  handleChange: noop,
  handleCertifyInformationChange: noop,
  handlePrimaryResidenceChange: noop,
  isPrimaryResidence: false,
  disableAll: false,
  disableConfirmation: false,
  submitButtonText: "Verify & Continue",
  termsAndConditions: false,
  handleChangeTermsAndConditions: noop,
  handleToggleTermsAndConditionsModal: noop,
};

export default VerifyAddressForm;
